// Shared, page-wide rotating tagline state.
//
// Why this exists:
//   - Hero + footer must always read the same tagline (no mismatch when
//     the user scrolls, then sees a different line at the bottom).
//   - It should advance every few seconds while the page is open, but
//     pause when the tab is hidden (battery + correctness) and respect
//     prefers-reduced-motion (accessibility).
//
// Implementation: a tiny module-level store with a Set of subscribers.
// The first hook to mount starts the interval; the last to unmount stops
// it. We persist the *current* index to sessionStorage so navigating
// between routes resumes from where the user left off instead of
// snapping back to the start.
import { useEffect, useState } from "react";
import { TAGLINES, getSessionTaglineIndex } from "@/lib/taglines";
import { trackTaglineImpression } from "@/lib/taglineTracker";

const ROTATE_MS = 6000;
const SESSION_KEY = "hl:tagline_idx:v1";

type Listener = (idx: number) => void;

let currentIdx = typeof window !== "undefined" ? getSessionTaglineIndex() : 0;
const listeners = new Set<Listener>();
let intervalId: ReturnType<typeof setInterval> | null = null;
let visibilityBound = false;

function persist(idx: number) {
  try {
    window.sessionStorage.setItem(SESSION_KEY, String(idx));
  } catch {
    /* ignore — sessionStorage may be unavailable */
  }
}

function tick() {
  currentIdx = (currentIdx + 1) % TAGLINES.length;
  persist(currentIdx);
  listeners.forEach((fn) => fn(currentIdx));
}

function start() {
  if (intervalId !== null) return;
  intervalId = setInterval(tick, ROTATE_MS);
}

function stop() {
  if (intervalId === null) return;
  clearInterval(intervalId);
  intervalId = null;
}

function bindVisibility() {
  if (visibilityBound || typeof document === "undefined") return;
  visibilityBound = true;
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      stop();
    } else if (listeners.size > 0) {
      start();
    }
  });
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Subscribe to the rotating tagline. All consumers see the same value
 * and rotate together. Returns the current tagline string.
 *
 * Pass the surface name ("hero" | "footer") so we can attribute
 * impressions to where the tagline was actually visible. Each (session,
 * tagline index, surface) combination is logged at most once.
 */
export function useRotatingTagline(surface: "hero" | "footer" = "hero"): string {
  const [idx, setIdx] = useState(currentIdx);

  useEffect(() => {
    // Honor reduced motion: still subscribe (so hero/footer stay synced
    // if something else advances the index), but don't auto-advance.
    const reduced = prefersReducedMotion();

    const listener: Listener = (next) => setIdx(next);
    listeners.add(listener);
    bindVisibility();

    if (!reduced && (typeof document === "undefined" || !document.hidden)) {
      start();
    }

    // Sync local state in case the module-level index advanced before
    // this consumer mounted (e.g. a route transition).
    setIdx(currentIdx);

    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) stop();
    };
  }, []);

  // Log an impression every time the tagline shown to the user changes.
  // The tracker dedupes per (session, idx, surface) so this is safe to
  // call on every render — at most one row per combination per session.
  useEffect(() => {
    trackTaglineImpression(idx, surface);
  }, [idx, surface]);

  return TAGLINES[idx];
}